import React, { useState } from "react";
import { handleApiError } from "@/utils/toastUtil";

import { Mail, CheckCircle } from "lucide-react";
import { Modal } from "./Modal";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../LanguageSelector";
import api from "../../api";
import { BASE_URL } from "../../config";
export function ForgotPassword() {
  const { t, i18n } = useTranslation(); // For translations
  const code = i18n.language;

  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleEmailSubmit = async (email) => {
    try {
      const response = await api.post(
        `${BASE_URL}/auth/forgot-password`,
        { email, code },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        return true;
      }
    } catch (error) {
      if (error.response?.status === 404) {
        return false;
      }
      console.error("Error during password reset:", error);
      handleApiError("forgot_password.error_general");
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t("forgot_password.error_invalid_email"));
      return;
    }

    setIsLoading(true);
    try {
      const userExists = await handleEmailSubmit(email);
      if (userExists) {
        setShowSuccessModal(true);
      } else {
        setError(t("forgot_password.error_user_not_found"));
      }
    } catch (err) {
      setError(t("forgot_password.error_general"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-subMain min-h-screen flex flex-col justify-center px-4 sm:px-6 lg:px-8 relative">
      <div className="absolute top-1 right-1 md:right-5">
        <LanguageSelector />
      </div>
      <div className="bg-subMain flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="mt-8 mx-auto w-full max-w-md">
          <div className="bg-white py-8 px-6 sm:px-10 shadow rounded-lg border border-border">
            <div className="mx-auto w-full max-w-md mb-10">
              <div className="flex justify-center">
                <Mail className="h-12 w-12 text-subMain" />
              </div>
              <h2 className="mt-6 text-center text-3xl font-extrabold text-main">
                {t("forgot_password.title")}
              </h2>
              <p className="mt-2 text-center text-sm text-textGray">
                {t("forgot_password.description")}
              </p>
            </div>
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-main">
                  {t("forgot_password.email_label")}
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-border rounded-md shadow-sm placeholder-textGray focus:outline-none focus:ring-subMain focus:border-subMain"
                    placeholder={t("forgot_password.email_placeholder")}
                  />
                </div>
              </div>

              {error && <div className="text-red-600 text-sm">{error}</div>}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-subMain hover:bg-subMain-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-subMain disabled:opacity-50 disabled:cursor-not-allowed">
                  {isLoading
                    ? t("forgot_password.submit_button_loading")
                    : t("forgot_password.submit_button")}
                </button>
              </div>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="relative flex justify-center text-sm">
                  <a
                    href="/"
                    className="text-subMain hover:text-subMain-dark transition">
                    {t("forgot_password.back_to_login")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Modal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}>
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-12 w-12 text-subMain" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t("forgot_password.modal_title")}
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              {t("forgot_password.modal_description", { email })}
            </p>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-subMain hover:bg-subMain-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              {t("forgot_password.modal_button")}
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
}
